import React, { useMemo } from 'react';
import { graphql } from 'gatsby';
import get from 'lodash/get';
import { IoLogoLinkedin } from 'react-icons/io5';
import { FaXTwitter } from 'react-icons/fa6';

import NewsCard from '../components/NewsCard';
import {
  PostContainer,
  Title,
  AuthorDescription,
  AuthorContainer,
  SocialLink,
} from '../styles/author.styled';
import SEO from '../components/seo';
import CustomPagination from '../components/customPagination';
import BannerGatsbyImg from '../components/BannerGatsbyImg';
import { getPrevNextPage } from '../utils/helper';
import StaticPicture from '../components/StaticImage';

export const Head = ({ data, pageContext }) => {
  const baseUrl = `/author/${pageContext.slug}/`;
  const { currentPage } = pageContext;
  const { totalPages } = pageContext;
  const author = get(data, 'allWpUser.nodes[0]');

  const { prevPage, nextPage } = getPrevNextPage(
    baseUrl,
    currentPage,
    totalPages,
  );

  const seoData = {
    title: `${get(author, 'name')} | Pandemic Action Network`,
    description: get(data, 'allWpPage.nodes[0].seo.metaDesc'),
    image: get(data, 'allWpPage.nodes[0].seo.opengraphImage.mediaItemUrl'),
    pageUrl: get(data, 'allWpPage.nodes[0].seo.opengraphUrl'),
    prevPage,
    nextPage,
    slug: currentPage === 1 ? baseUrl : `${baseUrl}${currentPage}/`,
    canonicalUrl: get(
      data,
      'allWpUser.nodes[0].canonicalUrlField.canonicalUrl',
    ),
  };

  return <SEO seoProps={seoData} />;
};

const Author = ({ data, pageContext }) => {
  const posts = get(data, 'allWpPost.nodes');
  const author = get(data, 'allWpUser.nodes[0]');
  const baseUrl = `/author/${pageContext.slug}/`;
  const { currentPage } = pageContext;
  const { totalPages } = pageContext;

  const authorBanner = get(
    data,
    'allWpPage.nodes[0].authorPageFields.authorBanner',
  );
  const authorSocialLinks = get(data, 'allWpUser.nodes[0].authorSocialLinks');
  const twitterSocialLink = get(authorSocialLinks, 'socialLinks.twitterLink');
  const linkdinSocialLink = get(authorSocialLinks, 'socialLinks.linkedinLink');

  const { authorContent } = get(data, 'allWpUser.nodes[0].author_page');
  console.log(authorContent, 'authorContent');

  const pagination = useMemo(() => {
    return (
      <CustomPagination
        baseUrl={baseUrl}
        currentPage={currentPage}
        totalPages={totalPages}
        sectionId='#author-title'
      />
    );
  }, [currentPage]);

  return (
    <>
      <BannerGatsbyImg
        title={get(author, 'name')}
        image={get(authorBanner, 'backgroundImage.localFile')}
        playbookNewsletter
      />
      <div className='wrapper'>
        <AuthorContainer>
          <AuthorDescription>
            <figure>
              <StaticPicture
                imgFile={get(author, 'avatar.url')}
                altText={get(author, 'name')}
                size='l'
              />
            </figure>
            <div>
              <p
                dangerouslySetInnerHTML={{ __html: get(author, 'description') }}
              />
              {twitterSocialLink || linkdinSocialLink ? (
                <SocialLink>
                  {twitterSocialLink && (
                    <li>
                      <span>
                        <FaXTwitter />
                      </span>
                      <a
                        href={twitterSocialLink}
                        title='Twitter'
                        target='_blank'
                        rel='noreferrer'
                        className='author-twitter-link'
                      >
                        Twitter
                      </a>
                    </li>
                  )}
                  {linkdinSocialLink && (
                    <li>
                      <span>
                        <IoLogoLinkedin />
                      </span>
                      <a
                        href={linkdinSocialLink}
                        title='LinkedIn'
                        target='_blank'
                        rel='noreferrer'
                        className='author-linkdin-link'
                      >
                        LinkedIn
                      </a>
                    </li>
                  )}
                </SocialLink>
              ) : null}
            </div>
          </AuthorDescription>
          {authorContent && (
            <>
              <Title>Videos</Title>
              <div
                dangerouslySetInnerHTML={{ __html: authorContent }}
                className='author-videos'
              />
            </>
          )}
          <Title id='author-title'>Recent posts</Title>
          <PostContainer>
            {Array.isArray(posts) &&
              posts.length > 0 &&
              posts.map((post) => {
                return (
                  <li key={post.id} className='news'>
                    <NewsCard {...post} noContent />
                  </li>
                );
              })}
          </PostContainer>
          {totalPages > 1 && pagination}
        </AuthorContainer>
      </div>
    </>
  );
};

export default Author;

export const AuthorQuery = graphql`
  query AuthorQuery($skip: Int!, $limit: Int!, $slug: String) {
    allWpPost(
      filter: {
        postsFields: { author: { elemMatch: { slug: { eq: $slug } } } }
      }
      limit: $limit
      skip: $skip
      sort: { date: DESC }
    ) {
      nodes {
        title
        id
        slug
        content
        postsFields {
          postSubTitle
          author {
            name
            slug
            id
          }
        }
        featuredImage {
          node {
            mediaItemUrl
            title
            localFile {
              childImageSharp {
                gatsbyImageData(formats: WEBP)
              }
            }
          }
        }
        date
        categories {
          nodes {
            name
            slug
            id
          }
        }
      }
    }
    allWpPage(filter: { slug: { eq: "author" } }) {
      nodes {
        seo {
          metaDesc
          title
          opengraphImage {
            mediaItemUrl
          }
          opengraphUrl
        }
        authorPageFields {
          authorBanner {
            backgroundImage {
              mediaItemUrl
              localFile {
                childImageSharp {
                  gatsbyImageData(placeholder: BLURRED, formats: WEBP)
                }
              }
            }
          }
        }
      }
    }
    allWpUser(filter: { slug: { eq: $slug } }) {
      nodes {
        name
        slug
        id
        email
        description
        canonicalUrlField {
          canonicalUrl
        }
        avatar {
          url
        }
        author_page {
          authorContent
        }
        authorSocialLinks {
          socialLinks {
            linkedinLink
            twitterLink
          }
        }
      }
    }
  }
`;
